import {
  Badge,
  Button,
  HStack,
  Text,
  Tooltip,
  useClipboard,
} from "@chakra-ui/react";
import { Trans, t } from "@lingui/macro";
import { DatePicker } from "@src/components/ui-kit";
import { CopyIcon } from "@src/components/ui-kit/HiddenCopyIcon";
import { Icon } from "@src/components/ui-kit/Icon";
import { TaskDeadline } from "@src/components/ui-kit/TaskItem/TaskItemComponents";
import { UncollapseOnHover } from "@src/components/ui-kit/UncollapseOnHover";
import { toApiDate } from "@src/utils/dates";
import { isDate } from "date-fns";
import { kebabCase } from "lodash";
import { observer } from "mobx-react-lite";
import { Fragment } from "react";
import {
  type ExpenseBudgetItemModel,
  isOurWorkBudgetItem,
  OurWorkBudgetItemModel,
} from "../models";
import { useStore } from "../Store";

export type ItemTitleProps = {
  item: OurWorkBudgetItemModel | ExpenseBudgetItemModel;
  onChangeDeadline?: (newDeadline: Date | null) => void;
  isDeadlineLoading?: boolean;
  canRenderDeadlineIcon?: boolean;
  tabIndex: number;
};

export const BUDGET_ITEM_HASH_INDEX_SEPARATOR = "-bii-";

const ItemTitle = ({
  item,
  onChangeDeadline,
  isDeadlineLoading,
  canRenderDeadlineIcon = true,
  tabIndex,
}: ItemTitleProps) => {
  const store = useStore();
  const hash = kebabCase(
    `${item.title}${BUDGET_ITEM_HASH_INDEX_SEPARATOR}${tabIndex}`,
  );
  const codeClipboard = useClipboard(item.item_code ?? "");
  const canAddDeadline =
    item instanceof OurWorkBudgetItemModel &&
    !item.deadline?.start &&
    canRenderDeadlineIcon;

  return (
    <HStack id={hash}>
      <UncollapseOnHover
        dataComponentName="budget-item-name"
        width={canAddDeadline ? "9" : "4"}
        hiddenComponent={
          <HStack spacing="1">
            <CopyIcon
              valueToCopy={`${location.origin}/projects/detail?id=${store.project?.id}#${hash}`}
            />
            {canAddDeadline && (
              <DatePicker
                selected={{ start: undefined, end: undefined }}
                usePortal
                trigger={
                  <Tooltip label={t`Add deadline`} shouldWrapChildren>
                    <Icon name="calendar" cursor="pointer" />
                  </Tooltip>
                }
                onChange={(deadline) => {
                  onChangeDeadline?.(deadline?.start ?? null);
                }}
              />
            )}
          </HStack>
        }
      >
        <Tooltip
          label={
            <Text>
              {item instanceof OurWorkBudgetItemModel && item.note && (
                <Fragment>
                  {item.note}
                  <br />
                  <br />
                </Fragment>
              )}
              {item.item_code}
              <br />
              <Trans>Click to copy budget item code</Trans>
            </Text>
          }
        >
          <Button
            textAlign="left"
            whiteSpace="break-spaces"
            colorScheme="blue"
            onClick={codeClipboard.onCopy}
            size="sm"
            variant="link"
          >
            {item.title}
          </Button>
        </Tooltip>
      </UncollapseOnHover>
      {Boolean(item.requested_for_deletion_at) && (
        <Tooltip
          label={
            isOurWorkBudgetItem(item) ? (
              <Trans>
                You still see this budget item because there is either credit
                invoiced or there is time tracked. You need to transfer
                remaining credit or transfer tracked time to other budget item.
              </Trans>
            ) : (
              <Trans>
                You still see this budget item because there is either credit
                invoiced or there are some expenses assigned. You need to
                transfer remaining credit or unassign expenses to other budget
                item.
              </Trans>
            )
          }
        >
          <Badge fontWeight="semibold" colorScheme="red">
            <Trans>Deleted</Trans>
          </Badge>
        </Tooltip>
      )}
      {item instanceof OurWorkBudgetItemModel &&
        isDate(item.deadline?.start) && (
          <TaskDeadline
            size="md"
            fontSize="xs"
            deadline={toApiDate(item.deadline.start)}
            onChange={onChangeDeadline}
            isLoading={isDeadlineLoading}
          />
        )}
    </HStack>
  );
};

export default observer(ItemTitle);
